import React, { useEffect, useMemo, useState } from 'react';
import { confirmPasswordReset } from '../../data/api';

// Password strength validation
const PASSWORD_RULES = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
};

interface PasswordStrength {
  isValid: boolean;
  strength: 'none' | 'weak' | 'medium' | 'strong';
  errors: string[];
}

function validatePasswordStrength(password: string): PasswordStrength {
  const errors: string[] = [];
  
  if (password.length < PASSWORD_RULES.minLength) {
    errors.push(`At least ${PASSWORD_RULES.minLength} characters`);
  }
  if (PASSWORD_RULES.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('One uppercase letter');
  }
  if (PASSWORD_RULES.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('One lowercase letter');
  }
  if (PASSWORD_RULES.requireNumbers && !/[0-9]/.test(password)) {
    errors.push('One number');
  }
  if (PASSWORD_RULES.requireSpecialChars && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('One special character');
  }

  const strength: PasswordStrength['strength'] = 
    password.length === 0 ? 'none' :
    errors.length > 2 ? 'weak' :
    errors.length > 0 ? 'medium' : 'strong';

  return {
    isValid: errors.length === 0,
    strength,
    errors,
  };
}

interface ResetPasswordScreenProps {
  uid: string;
  token: string;
  onSuccess: () => void;
  onRequestNewLink: () => void;
}

export const ResetPasswordScreen: React.FC<ResetPasswordScreenProps> = ({ 
  uid, 
  token, 
  onSuccess,
  onRequestNewLink,
}) => {
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw1, setShowPw1] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState({ pw1: false, pw2: false });
  const [tokenExpired, setTokenExpired] = useState(false);

  // Auto-redirect after success
  useEffect(() => {
    if (submitted) {
      const timer = setTimeout(() => {
        onSuccess();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [submitted, onSuccess]);

  const passwordStrength = useMemo(() => validatePasswordStrength(pw1), [pw1]);
  const passwordsMatch = pw1 && pw2 && pw1 === pw2;
  const showMatchError = touched.pw2 && pw2 && !passwordsMatch;

  const canSubmit = uid && token && passwordStrength.isValid && passwordsMatch && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setError('');
    setTokenExpired(false);
    setLoading(true);
    try {
      await confirmPasswordReset({ uid, token, new_password: pw1 });
      setSubmitted(true);
    } catch (e: any) {
      const errorMsg = e?.message || 'Reset failed. Please try again or request a new link.';
      setError(errorMsg);
      
      // Detect expired/invalid token errors
      if (errorMsg.toLowerCase().includes('expired') || 
          errorMsg.toLowerCase().includes('invalid') ||
          errorMsg.toLowerCase().includes('token')) {
        setTokenExpired(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const strengthColor = {
    none: '#999',
    weak: '#c41e3a',
    medium: '#f59e0b',
    strong: '#10b981',
  }[passwordStrength.strength];

  return (
    <main className="card form-card" role="main" aria-labelledby="resetPasswordTitle">
      <img src="/avcs-logo.png" alt="AVCS Logo" className="logo" />
      <span className="tag">Reset password</span>
      <h1 id="resetPasswordTitle">Set a new password</h1>
      
      {!submitted && (
        <>
          <p className="desc">
            You opened this page from a password reset email. Create a strong password below to secure your account.
          </p>
          
          <div aria-live="polite">
            {error && (
              <div className="alert error" role="alert">
                {tokenExpired ? (
                  <>
                    <strong>This password reset link has expired or is invalid.</strong>
                    <br />
                    Please request a new one to continue.
                  </>
                ) : (
                  error
                )}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="form" aria-label="Reset password form">
            {/* New Password */}
            <label>
              <span>New Password</span>
              <div className="pw-field">
                <input
                  type={showPw1 ? 'text' : 'password'}
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, pw1: true }))}
                  placeholder="Enter new password"
                  required
                  autoFocus
                  autoComplete="new-password"
                  minLength={PASSWORD_RULES.minLength}
                  aria-describedby="password-requirements"
                />
                <button 
                  type="button" 
                  className="btn tiny" 
                  onClick={() => setShowPw1(v => !v)}
                  aria-pressed={showPw1}
                  aria-label={showPw1 ? 'Hide password' : 'Show password'}
                >
                  {showPw1 ? 'Hide' : 'Show'}
                </button>
              </div>
              
              {/* Password strength indicator */}
              {touched.pw1 && pw1 && (
                <div style={{ marginTop: '8px' }}>
                  <small 
                    style={{ 
                      color: strengthColor,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <span 
                      style={{ 
                        width: '8px', 
                        height: '8px', 
                        borderRadius: '50%', 
                        background: strengthColor,
                      }}
                    />
                    Password strength: <strong>{passwordStrength.strength}</strong>
                  </small>
                  {passwordStrength.errors.length > 0 && (
                    <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
                      Missing: {passwordStrength.errors.join(', ')}
                    </small>
                  )}
                </div>
              )}
            </label>

            {/* Confirm Password */}
            <label>
              <span>Confirm New Password</span>
              <div className="pw-field">
                <input
                  type={showPw2 ? 'text' : 'password'}
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, pw2: true }))}
                  placeholder="Re-enter new password"
                  required
                  autoComplete="new-password"
                  minLength={PASSWORD_RULES.minLength}
                />
                <button 
                  type="button" 
                  className="btn tiny" 
                  onClick={() => setShowPw2(v => !v)}
                  aria-pressed={showPw2}
                  aria-label={showPw2 ? 'Hide password' : 'Show password'}
                >
                  {showPw2 ? 'Hide' : 'Show'}
                </button>
              </div>
              
              {/* Match validation */}
              {showMatchError && (
                <small style={{ color: '#c41e3a', marginTop: '4px', display: 'block' }}>
                  ⚠️ Passwords do not match
                </small>
              )}
              {touched.pw2 && passwordsMatch && (
                <small style={{ color: '#10b981', marginTop: '4px', display: 'block' }}>
                  ✓ Passwords match
                </small>
              )}
            </label>

            {/* Password requirements */}
            <div id="password-requirements" className="hint" style={{ fontSize: '13px', lineHeight: '1.6' }}>
              <strong>Password requirements:</strong>
              <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                <li>At least 8 characters</li>
                <li>Include uppercase and lowercase letters</li>
                <li>Include at least one number</li>
                <li>Include at least one special character (!@#$%^&*)</li>
              </ul>
            </div>

            <div className="actions">
              <button 
                className="btn primary" 
                type="submit" 
                disabled={!canSubmit}
                aria-busy={loading}
              >
                {loading ? 'Resetting password…' : 'Reset Password'}
              </button>
              
              {tokenExpired && (
                <button 
                  className="btn" 
                  type="button" 
                  onClick={onRequestNewLink}
                >
                  Request New Reset Link
                </button>
              )}
            </div>
          </form>
        </>
      )}
      
      {submitted && (
        <div className="success-state">
          <div className="alert success" role="status">
            <strong>Your password has been reset successfully!</strong>
            <br />
            Redirecting you to the login screen...
          </div>
          <div className="actions">
            <button className="btn primary" onClick={onSuccess}>
              Go to Sign In Now
            </button>
          </div>
        </div>
      )}
    </main>
  );
};
